import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  DrawMode,
  DrawType,
  RewardRequest,
  rewardsService,
  RewardStatus,
} from '@/services/rewards.service';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

interface RewardFormModalProps {
  open: boolean;
  onClose: () => void;
  reward?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export function RewardFormModal({
  open,
  onClose,
  reward,
}: RewardFormModalProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<RewardRequest>({
    title: '',
    description: '',
    shortDescription: null,
    imageUrl: null,
    thumbnailUrl: null,
    galleryImages: [],
    tokensRequired: 0,
    drawType: DrawType.Automatic,
    drawMode: DrawMode.Random,
    maxParticipants: null,
    winnerCount: 1,
    startDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    endDate: format(
      new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      "yyyy-MM-dd'T'HH:mm",
    ),
    status: RewardStatus.Draft,
    category: null,
    tags: [],
    rules: null,
    isPublic: true,
    isFeatured: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Populate form when editing
  useEffect(() => {
    if (reward) {
      setFormData({
        title: reward.title,
        description: reward.description,
        shortDescription: reward.shortDescription,
        imageUrl: reward.imageUrl,
        thumbnailUrl: reward.thumbnailUrl,
        galleryImages: reward.galleryImages || [],
        tokensRequired: reward.tokensRequired,
        drawType: reward.drawType,
        drawMode: reward.drawMode,
        maxParticipants: reward.maxParticipants,
        winnerCount: reward.winnerCount,
        startDate: format(new Date(reward.startDate), "yyyy-MM-dd'T'HH:mm"),
        endDate: format(new Date(reward.endDate), "yyyy-MM-dd'T'HH:mm"),
        status: reward.status,
        category: reward.category,
        tags: reward.tags || [],
        rules: reward.rules,
        isPublic: reward.isPublic,
        isFeatured: reward.isFeatured,
      });
    }
    setErrors({});
  }, [reward, open]);

  const saveMutation = useMutation({
    mutationFn: (data: RewardRequest) =>
      reward
        ? rewardsService.updateReward(reward.id, data)
        : rewardsService.createReward(data),
    onSuccess: () => {
      toast.success(`Reward ${reward ? 'updated' : 'created'} successfully`);
      queryClient.invalidateQueries({ queryKey: ['rewards'] });
      handleClose();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to save reward';
      toast.error(message);
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (formData.tokensRequired <= 0) {
      newErrors.tokensRequired = 'Tokens required must be greater than 0';
    }

    if (formData.winnerCount <= 0) {
      newErrors.winnerCount = 'Winner count must be greater than 0';
    }

    if (formData.maxParticipants !== null && formData.maxParticipants <= 0) {
      newErrors.maxParticipants = 'Max participants must be greater than 0';
    }

    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    if (end <= start) {
      newErrors.endDate = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    saveMutation.mutate(formData);
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      shortDescription: null,
      imageUrl: null,
      thumbnailUrl: null,
      galleryImages: [],
      tokensRequired: 0,
      drawType: DrawType.Automatic,
      drawMode: DrawMode.Random,
      maxParticipants: null,
      winnerCount: 1,
      startDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      endDate: format(
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        "yyyy-MM-dd'T'HH:mm",
      ),
      status: RewardStatus.Draft,
      category: null,
      tags: [],
      rules: null,
      isPublic: true,
      isFeatured: false,
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{reward ? 'Edit Reward' : 'Create Reward'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">
              Title <span className="text-danger">*</span>
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              placeholder="e.g., Grand Prize Draw"
            />
            {errors.title && (
              <p className="text-sm text-danger">{errors.title}</p>
            )}
          </div>

          {/* Short Description */}
          <div className="space-y-2">
            <Label htmlFor="shortDescription">Short Description</Label>
            <Input
              id="shortDescription"
              value={formData.shortDescription || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  shortDescription: e.target.value || null,
                })
              }
              placeholder="Brief summary"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">
              Description <span className="text-danger">*</span>
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Detailed description of the reward..."
              rows={4}
            />
            {errors.description && (
              <p className="text-sm text-danger">{errors.description}</p>
            )}
          </div>

          {/* Grid: Tokens, Winners, Max Participants */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tokensRequired">
                Tokens Required <span className="text-danger">*</span>
              </Label>
              <Input
                id="tokensRequired"
                type="number"
                min="1"
                value={formData.tokensRequired}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tokensRequired: parseInt(e.target.value) || 0,
                  })
                }
              />
              {errors.tokensRequired && (
                <p className="text-sm text-danger">{errors.tokensRequired}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="winnerCount">
                Winner Count <span className="text-danger">*</span>
              </Label>
              <Input
                id="winnerCount"
                type="number"
                min="1"
                value={formData.winnerCount}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    winnerCount: parseInt(e.target.value) || 1,
                  })
                }
              />
              {errors.winnerCount && (
                <p className="text-sm text-danger">{errors.winnerCount}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxParticipants">Max Participants</Label>
              <Input
                id="maxParticipants"
                type="number"
                min="0"
                value={formData.maxParticipants ?? ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    maxParticipants: e.target.value
                      ? parseInt(e.target.value)
                      : null,
                  })
                }
                placeholder="Unlimited"
              />
              {errors.maxParticipants && (
                <p className="text-sm text-danger">{errors.maxParticipants}</p>
              )}
            </div>
          </div>

          {/* Draw Type and Mode */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="drawType">Draw Type</Label>
              <Select
                value={String(formData.drawType)}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    drawType: parseInt(value) as DrawType,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(DrawType.Automatic)}>
                    Automatic
                  </SelectItem>
                  <SelectItem value={String(DrawType.Manual)}>
                    Manual
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="drawMode">Draw Mode</Label>
              <Select
                value={String(formData.drawMode)}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    drawMode: parseInt(value) as DrawMode,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(DrawMode.Random)}>
                    Random
                  </SelectItem>
                  <SelectItem value={String(DrawMode.Lottery)}>
                    Lottery
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Status - Only shown when editing */}
          {reward && (
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={String(formData.status)}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    status: parseInt(value) as RewardStatus,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(RewardStatus.Draft)}>
                    Draft
                  </SelectItem>
                  <SelectItem value={String(RewardStatus.Active)}>
                    Active
                  </SelectItem>
                  <SelectItem value={String(RewardStatus.Closed)}>
                    Closed
                  </SelectItem>
                  <SelectItem value={String(RewardStatus.Drawn)}>
                    Drawn
                  </SelectItem>
                  <SelectItem value={String(RewardStatus.Cancelled)}>
                    Cancelled
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Start and End Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">
                Start Date <span className="text-danger">*</span>
              </Label>
              <Input
                id="startDate"
                type="datetime-local"
                value={formData.startDate}
                onChange={(e) =>
                  setFormData({ ...formData, startDate: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="endDate">
                End Date <span className="text-danger">*</span>
              </Label>
              <Input
                id="endDate"
                type="datetime-local"
                value={formData.endDate}
                onChange={(e) =>
                  setFormData({ ...formData, endDate: e.target.value })
                }
              />
              {errors.endDate && (
                <p className="text-sm text-danger">{errors.endDate}</p>
              )}
            </div>
          </div>

          {/* Image URLs */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="imageUrl">Image URL</Label>
              <Input
                id="imageUrl"
                value={formData.imageUrl || ''}
                onChange={(e) =>
                  setFormData({ ...formData, imageUrl: e.target.value || null })
                }
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="thumbnailUrl">Thumbnail URL</Label>
              <Input
                id="thumbnailUrl"
                value={formData.thumbnailUrl || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    thumbnailUrl: e.target.value || null,
                  })
                }
                placeholder="https://example.com/thumb.jpg"
              />
            </div>
          </div>

          {/* Category and Rules */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                value={formData.category || ''}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value || null })
                }
                placeholder="e.g., Electronics, Gift Cards"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rules">Rules</Label>
              <Input
                id="rules"
                value={formData.rules || ''}
                onChange={(e) =>
                  setFormData({ ...formData, rules: e.target.value || null })
                }
                placeholder="Draw rules and conditions"
              />
            </div>
          </div>

          {/* Switches */}
          <div className="flex gap-6">
            <div className="flex items-center space-x-2">
              <Switch
                id="isPublic"
                checked={formData.isPublic}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, isPublic: checked })
                }
              />
              <Label htmlFor="isPublic" className="cursor-pointer">
                Public (visible to all users)
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="isFeatured"
                checked={formData.isFeatured}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, isFeatured: checked })
                }
              />
              <Label htmlFor="isFeatured" className="cursor-pointer">
                Featured
              </Label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saveMutation.isPending}>
            {saveMutation.isPending
              ? 'Saving...'
              : reward
                ? 'Update'
                : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
